import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import _ from 'lodash';
import type Rollbar from 'rollbar';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import {
  LogInPaths,
  REMOVED_SELF_SERVE_PATHS,
} from '@bbkAdminComponents/log-in-flow/log-in-paths';
import type * as LogInTypes from '@bbkAdminComponents/log-in-flow/log-in-types';
import { SignIn } from '@bbkAdminComponents/log-in-flow/sign-in';
import { PreparingOverlay } from '@bbkAdminComponents/preparing-overlay/preparing-overlay';
import { type CompanyData } from '@bbkAdminComponents/trial-signup/TrialSignupContext';
import { type ActiveUser } from '@bbkAdminRedux/app/reducers';
import { useSignInRedirect } from '@bbkAdminRedux/app/useSignInRedirect';
import { selectLastActive } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import {
  brightbackAuthSlice,
  SignInType,
} from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { PLANS } from '@bbkAdminRedux/rtkq/checkout.plans';
import { useSetBBAuthenticityToken } from '@bbkAdminRedux/rtkq/current-user.slice';
import {
  IdentityProviderEnum,
  preauthCompanySlice,
  type CbIdpRegion,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { SignupPaths } from '@bbkAdminRouter/signup-paths';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { toSafeInternalRedirect } from '@bbkAdminUtils/safe-redirect';
import { useBodyScrollMode } from '@bbkAdminUtils/use-body-scroll-mode';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from '@bbkShared/react-router';

export const LogInRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastActive = useSelector(selectLastActive);
  const searchParams = useSearchParams<
    {
      invite: string;
      email: string;
      tmp: string;
      redirectUri: string;
      // next three fields are specific to initiating signIn flow from chargebee Product Switcher
      idp_hint: IdentityProviderEnum.CHARGEBEE_IDP;
      login_hint: string;
      region_hint: CbIdpRegion;
      forward: string;
    } & CompanyData
  >();
  const emailQuery = searchParams.email ?? '';
  const tmpPWQuery = searchParams.tmp;
  const cbQuery = searchParams.cb_email;
  const redirectUri = searchParams.redirectUri;
  const search = location.search;
  const setBBAuthenticityToken = useSetBBAuthenticityToken();
  const { url: signInRedirectUri } = useSignInRedirect();

  // Sign in
  const [openSignIn$] = brightbackAuthSlice.endpoints.openSignIn.useMutation();
  const [signIn$] = brightbackAuthSlice.endpoints.signIn.useMutation();

  // Sign out
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();

  useEffect(() => {
    if (searchParams.idp_hint === IdentityProviderEnum.CHARGEBEE_IDP) {
      openSignIn$({
        type: SignInType.CHARGEBEE,
        email: searchParams.login_hint,
        redirect: searchParams.forward,
        region: searchParams.region_hint,
      });
    }
  }, []);

  const [email, setEmail] = useState<string>(
    emailQuery || lastActive?.email || ''
  );
  const [password, setPassword] = useState<string>(tmpPWQuery || '');
  const [error, setError] = useState<LogInTypes.Error>();
  const [sending, setSending] = useState(false);
  const [showOverLay, setShowOverLay] = useState<boolean>(false);
  const [cacheCompanyCreation] =
    preauthCompanySlice.endpoints.cacheCompanyCreation.useMutation();
  const [createCompanyFromCache] =
    preauthCompanySlice.endpoints.createCompanyFromCache.useMutation();

  const { data: chargebeeCachedData } =
    preauthCompanySlice.endpoints.fetchCachedDataFromChargebee.useQuery(
      cbQuery ? { token: cbQuery } : skipToken
    );

  const signupPlan = chargebeeCachedData?.subscription_plan;
  const isStarter =
    signupPlan === PLANS.STARTER_FREE || signupPlan === PLANS.STARTER_PREMIUM;

  useEffect(() => {
    if (cbQuery) setEmail(cbQuery);
  }, [cbQuery]);

  useEffect(() => {
    const init = async () => {
      if (location.pathname === LogInPaths.logout) {
        await signOut$({ redirect: LogInPaths.signIn }).unwrap();
      }
    };

    // A sign-out that fails leaves the session live, so tell the user instead
    // of dropping the rejection on the floor.
    init().catch((e) => {
      GlobalLogger.warn(e as Rollbar.LogArgument);
      setError({
        title: 'Sign out failed',
        message: 'Your session is still active. Please try again.',
      });
    });
  }, [location.pathname, signOut$]);

  useBodyScrollMode('allow');

  useEffect(() => {
    const alert = BrightbackSession.getAuthAlert();
    if (alert) {
      BrightbackSession.setAuthAlert(null);
      BlackToastTrigger({
        content: `Session ended. ${alert}`,
        options: { toastId: 'sessionEndedAlert' },
      });
    }
  }, []);

  const formValues = {
    email,
    setEmail,
    password,
    setPassword,
  };

  const checkCompanyCreation = async (em: string) => {
    if (searchParams?.companyName) {
      const { companyName, domain, additionalParams } = searchParams;

      if (!companyName || !domain) {
        throw Error('Company fields cannot be blank');
      }
      await cacheCompanyCreation({
        email: em,
        company_name: companyName,
        domain,
        additional_params: additionalParams || undefined,
      }).unwrap();
    }

    await createCompanyFromCache()
      .unwrap()
      .then(({ companyKey, appKey }) => {
        if (!companyKey || !_.isString(companyKey)) {
          // A normal login (no company-creation params) carrying a validated
          // same-origin redirectUri must still land there. Post-login
          // validation no longer populates the global current-user query, so
          // there is no in-place AuthorizedRouter redirect to fall back on;
          // navigate to the target directly instead of dropping to /company
          // (which, for a multi-company user, bounces to the selector).
          if (redirectUri && !searchParams?.companyName) {
            window.location.assign(
              toSafeInternalRedirect(redirectUri, LogInPaths.company)
            );
          } else {
            window.location.pathname = `/company`;
          }
          return;
        }

        if (!isStarter && searchParams?.companyName) {
          // company creation path
          navigate({
            pathname: SignupPaths.trialSignup3,
            search: encodeObjectForQuery({ companyKey, ...searchParams }),
          });
          return;
        } else {
          // no company creation - normal login
          if (redirectUri) {
            // `redirectUri` may carry a query string and can be attacker-supplied
            // via `?redirectUri=`, so validate same-origin and use assign() rather
            // than setting location.pathname (which mangles query/hash).
            window.location.assign(
              toSafeInternalRedirect(redirectUri, LogInPaths.company)
            );
          } else {
            window.location.pathname =
              companyKey && appKey
                ? `/company/${companyKey}/app/${appKey}`
                : '/company';
          }
        }
      })
      .catch((err) => {
        GlobalLogger.error(err);
        window.location.reload();
      });
  };

  const chargebeeSignIn = async (em?: string, cbLoginBaseUrl?: string) => {
    try {
      setShowOverLay(true);
      BlackToastTrigger({ content: 'Signing you in.' });
      const tokens = await signIn$({
        type: SignInType.CHARGEBEE,
        email: em,
        cbLoginBaseUrl,
      }).unwrap();
      if (!tokens) {
        throw new Error('Failed to sign in via Chargebee');
      }
      const user = await setBBAuthenticityToken();
      if (!user) {
        // Reauth exhausted its retries (a toast was already surfaced). Stop
        // instead of dereferencing an undefined user.
        setShowOverLay(false);
        return;
      }
      await checkCompanyCreation(user.email);
    } catch (e) {
      setShowOverLay(false);
      GlobalLogger.warn(e as Rollbar.LogArgument);
    }
  };

  const performSignIn = async () => {
    try {
      // Dev-identity login is the only remaining password-based flow; Cognito
      // username/password sign-in has been removed.
      await signIn$({
        type: SignInType.DEV,
        email,
        pw: password,
      }).unwrap();
    } catch (err) {
      const e = err as LogInTypes.Error;
      setSending(false);
      setError(e);
      throw e;
    }
    return true;
  };

  const signIn = async (
    e?:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setShowOverLay(true);
    setSending(true);
    e?.preventDefault();
    try {
      if (!(await performSignIn())) {
        return;
      }
    } catch (err) {
      setShowOverLay(false);
      GlobalLogger.warn(`Sign-in Error: user ${email} failed to sign-in`, err);
      return;
    }

    let user: ActiveUser | undefined;
    try {
      user = await setBBAuthenticityToken();
    } catch (err) {
      setShowOverLay(false);
      setSending(false);
      GlobalLogger.warn(
        `Sign-in Error: user ${email} failed to get bbk authenticity token`,
        err
      );
      return;
    }

    if (!user) {
      // Reauth exhausted its retries (a toast was already surfaced). Re-enable
      // the form (reset `sending`) so the user can retry without a reload, and
      // do not proceed to company creation with a dead session.
      setShowOverLay(false);
      setSending(false);
      return;
    }

    try {
      await checkCompanyCreation(user.email);
    } catch (err) {
      setShowOverLay(false);
      GlobalLogger.warn(
        `Sign-in Error: an error occurred while user ${email} created company ${searchParams?.companyName} from cache`,
        err
      );
      window.location.assign(toSafeInternalRedirect(redirectUri, '/company'));
    }
  };

  return (
    <div className="bbk-authRoot tw-relative tw-min-h-screen tw-bg-neutral-50">
      <Routes>
        <Route
          path="/company"
          element={
            <Navigate replace to={{ pathname: LogInPaths.signIn, search }} />
          }
        />
        <Route
          path={`${LogInPaths.signIn}/*`}
          element={
            !showOverLay ? (
              <SignIn
                formValues={formValues}
                chargebeeSignIn={chargebeeSignIn}
                signIn={signIn}
                error={error}
                sending={sending}
              />
            ) : (
              <PreparingOverlay presentation="sting" message="Signing you in" />
            )
          }
        />
        {/* Keep sign-in ahead of protected-app matches, as in the legacy Switch. */}
        <Route
          path="*"
          element={
            <Routes>
              <Route
                path="/company/:companyName/app/:appId/*"
                element={
                  <Navigate
                    replace
                    to={{
                      pathname: LogInPaths.signIn,
                      search: encodeObjectForQuery({
                        redirectUri: location.pathname + location.search,
                      }),
                    }}
                  />
                }
              />
              {REMOVED_SELF_SERVE_PATHS.map((path) => (
                <Route
                  key={path}
                  path={`${path}/*`}
                  element={<Navigate replace to={LogInPaths.signIn} />}
                />
              ))}
              <Route
                path="*"
                element={<Navigate replace to={signInRedirectUri} />}
              />
            </Routes>
          }
        />
      </Routes>
    </div>
  );
};
